import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAuthContext } from '../UserAuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Secciones/Header';
import Footer from '../components/Secciones/Footer';
import './Cart.css';
import EmptyCart from '../assets/images/EmptyCart.png';
import { getGuestCart, removeFromGuestCart, removeManyFromGuestCart } from '../utils/guestCart';

function Cart() {
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [moreDetails, setMoreDetails] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [shipping, setShipping] = useState(10);
  const [tax, setTax] = useState(0);
  const [specialFees, setSpecialFees] = useState([]);
  const { user, loading } = useContext(UserAuthContext);
  const navigate = useNavigate();

  // Utility to construct image URL
  const getImageUrl = (filename) => {
    if (!filename || filename === 'none' || filename === null) {
      console.warn('No valid filename provided, using placeholder');
      return 'https://valleyapi-dr9y.onrender.com/data/placeholder.jpg';
    }
    const url = `https://valleyapi-dr9y.onrender.com/data/${filename}`;
    return url;
  };

  // Utility to truncate text
  const truncateText = (text, maxLength) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Fetch cart items and user data (including address fields)
  const fetchCartItems = async () => {
    if (!user || !user.userid) {
      const guestItems = getGuestCart().map(item => ({
        productId: item.idproducts,
        name: item.product_name || 'Unknown Product',
        description: item.product_description || 'No description',
        brand: item.product_brand || 'Unknown Brand',
        price: parseFloat(item.product_price) || 0,
        imageUrl: item.product_image,
        quantity: parseInt(item.quantity, 10) || 1,
        stock: item.product_stock != null ? parseInt(item.product_stock, 10) : 0,
      }));
      setCartItems(guestItems);
      setIsLoading(false);
      return;
    }

    const token = localStorage.getItem('user_token');
    if (!token) {
      toast.error('Session expired. Please log in again.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      // Send the user home and auto-open the header's login panel instead of
      // the registration form (there is no dedicated /login route).
      try { sessionStorage.setItem('valley_open_auth', '1'); } catch { /* storage unavailable */ }
      navigate('/');
      return;
    }

    // Move the browser cart into the authenticated cart after login/signup.
    // Each item is migrated independently (Promise.allSettled) so a single
    // failed transfer doesn't block the rest, and only the items that
    // actually succeeded are cleared from the guest cart -- otherwise a
    // retry on the next visit would re-send items the server already has,
    // duplicating them in the user's cart.
    const guestCart = getGuestCart();
    if (guestCart.length > 0) {
      const migrationResults = await Promise.allSettled(guestCart.map(item => fetch('https://valleyapi-dr9y.onrender.com/api/cart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ idproducts: item.idproducts, quantity: item.quantity || 1 }),
      }).then(async response => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Could not transfer ${item.product_name || 'guest cart item'}`);
        }
        return response;
      })));

      const migratedProductIds = guestCart
        .filter((_, index) => migrationResults[index].status === 'fulfilled')
        .map(item => item.idproducts);
      const failedCount = migrationResults.filter(result => result.status === 'rejected').length;

      if (migratedProductIds.length > 0) {
        removeManyFromGuestCart(migratedProductIds);
      }
      if (failedCount > 0) {
        const failureMessages = migrationResults
          .filter((result) => result.status === 'rejected')
          .map((result) => result.reason?.message)
          .filter(Boolean);
        console.error(`Error transferring ${failedCount} guest cart item(s):`, failureMessages);
        toast.warn('Some items from your guest cart could not be transferred yet. They remain saved in this browser.');
      }
    }

    setIsLoading(true);
    try {
      let userCountry = 'Unknown';
      let userBillingAddress = '';
      let userZipCode = '';
      let userShippingAddress = '';
      try {
        const userResponse = await fetch('https://valleyapi-dr9y.onrender.com/api/users', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!userResponse.ok) {
          const errorData = await userResponse.json();
          console.error('Failed to fetch user data:', errorData);
          throw new Error(errorData.message || 'Failed to fetch user data');
        }

        const userData = await userResponse.json();
        userCountry = userData.country || 'Unknown';
        userBillingAddress = userData.billing_address || '';
        userZipCode = userData.zipCode || '';
        userShippingAddress = userData.shipping_address || '';
      } catch (userError) {
        console.error('Error fetching user data:', userError.message);
        toast.warn('Could not fetch user data, using defaults.', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
        });
      }
      setBillingAddress(userBillingAddress);
      setZipCode(userZipCode);
      setShippingAddress(userShippingAddress);

      let shippingCost = 10;
      let taxRate = 0;
      try {
        const countryResponse = await fetch('https://valleyapi-dr9y.onrender.com/api/countries', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!countryResponse.ok) {
          console.error('Failed to fetch countries:', countryResponse.status);
          throw new Error('Failed to fetch countries');
        }

        const countries = await countryResponse.json();
        const countryData = countries.find(c => c.Country === userCountry);
        if (countryData) {
          shippingCost = countryData.Shipping_Cost != null ? parseFloat(countryData.Shipping_Cost) : 10;
          taxRate = countryData.Taxes != null ? parseFloat(countryData.Taxes) : 0;
        } else {
          toast.warn(`No shipping/tax data for ${userCountry}, using defaults.`, {
            position: 'top-right',
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
          });
        }
      } catch (countryError) {
        console.error('Error fetching country data:', countryError.message);
        toast.warn('Could not fetch country data, using default shipping/tax.', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
        });
      }
      setShipping(Number.isFinite(shippingCost) ? shippingCost : 10);
      setTax(Number.isFinite(taxRate) ? taxRate : 0);

      // Fetch special fees
      try {
        const feesResponse = await fetch('https://valleyapi-dr9y.onrender.com/api/cart/pricing-fees', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!feesResponse.ok) {
          const errorData = await feesResponse.json();
          console.error('Failed to fetch special fees:', errorData);
          throw new Error(errorData.error || 'Failed to fetch special fees');
        }

        const feesData = await feesResponse.json();
        const validFees = feesData.filter(fee => {
          const isValid = fee.brand && Number.isFinite(parseFloat(fee.fee)) && ['Porcentage', 'USD'].includes(fee.fee_type);
          if (!isValid) {
            console.warn(`Invalid special fee data: ${JSON.stringify(fee)}`);
          }
          return isValid;
        });
        setSpecialFees(validFees);
      } catch (feesError) {
        console.error('Error fetching special fees:', feesError.message);
        toast.warn('Could not fetch special fees, tariffs will be zero.', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
        });
        setSpecialFees([]);
      }

      const response = await fetch('https://valleyapi-dr9y.onrender.com/api/cart', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to fetch cart items:', errorData);
        throw new Error(errorData.message || 'Failed to fetch cart items');
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        console.error('API response is not an array:', data);
        throw new Error('Invalid API response format');
      }

      const mappedItems = data
        .filter(item => {
          const isValid = item.product_price != null && item.quantity != null && Number.isFinite(parseFloat(item.product_price)) && Number.isInteger(parseInt(item.quantity, 10));
          if (!isValid) {
            console.warn(`Invalid cart item data: ${JSON.stringify(item)}`);
            toast.warn(`Skipping invalid item: ${item.product_name || 'Unknown'}`, {
              position: 'top-right',
              autoClose: 2000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: false,
              draggable: true,
            });
          }
          return isValid;
        })
        .map(item => {
          if (item.stockWarning) {
            toast.warn(item.stockWarning, {
              position: 'top-right',
              autoClose: 2000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: false,
              draggable: true,
            });
          }
          return {
            productId: item.idproducts,
            name: item.product_name || 'Unknown Product',
            description: item.product_description || 'No description',
            brand: item.product_brand || 'Unknown Brand',
            price: parseFloat(item.product_price) || 0,
            imageUrl: item.product_image,
            quantity: parseInt(item.quantity, 10) || 1,
            stock: item.product_stock != null ? parseInt(item.product_stock, 10) : 0,
            stockWarning: item.stockWarning || null,
          };
        });
      setCartItems(mappedItems);
    } catch (error) {
      console.error('Error fetching cart items:', error.message);
      toast.error(`Error: ${error.message}`, {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      setCartItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete item
  const handleDeleteItem = async (productId, productName) => {
    if (!user) {
      setCartItems(removeFromGuestCart(productId).map(item => ({
        productId: item.idproducts,
        name: item.product_name || 'Unknown Product',
        description: item.product_description || 'No description',
        brand: item.product_brand || 'Unknown Brand',
        price: parseFloat(item.product_price) || 0,
        imageUrl: item.product_image,
        quantity: parseInt(item.quantity, 10) || 1,
        stock: item.product_stock != null ? parseInt(item.product_stock, 10) : 0,
      })));
      toast.success(`Removed ${truncateText(productName, 30)} from cart!`, {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      return;
    }

    const token = localStorage.getItem('user_token');
    if (!token) {
      toast.error('Session expired. Please log in again.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      // Send the user home and auto-open the header's login panel instead of
      // the registration form (there is no dedicated /login route).
      try { sessionStorage.setItem('valley_open_auth', '1'); } catch { /* storage unavailable */ }
      navigate('/');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`https://valleyapi-dr9y.onrender.com/api/cart/${productId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to delete cart item:', errorData);
        throw new Error(errorData.message || 'Failed to delete cart item');
      }

      const data = await response.json();
      setCartItems(prevItems => prevItems.filter(item => item.productId !== productId));
      toast.success(`Removed ${truncateText(productName, 30)} from cart!`, {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });

      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      console.error('Error deleting cart item:', error.message);
      toast.error('Failed to delete item from cart. Please try again.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle address field changes
  const handleBillingAddressChange = (e) => {
    const value = e.target.value.slice(0, 255);
    setBillingAddress(value);
  };

  const handleZipCodeChange = (e) => {
    const value = e.target.value.slice(0, 20);
    setZipCode(value);
  };

  const handleShippingAddressChange = (e) => {
    const value = e.target.value.slice(0, 255);
    setShippingAddress(value);
  };

  // Handle checkout
  const handleCheckout = async () => {
    if (!user || !user.userid) {
      toast.info('Create an account or log in to continue with payment.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      navigate('/signup', { state: { returnTo: '/cart' } });
      return;
    }

    const token = localStorage.getItem('user_token');
    if (!token) {
      toast.error('Session expired. Please log in again.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      // Send the user home and auto-open the header's login panel instead of
      // the registration form (there is no dedicated /login route).
      try { sessionStorage.setItem('valley_open_auth', '1'); } catch { /* storage unavailable */ }
      navigate('/');
      return;
    }

    // Ensure cartItems are valid
    if (cartItems.length === 0) {
      toast.error('Cart is empty. Please add items before checking out.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      return;
    }

    const subtotal = cartItems.reduce((acc, item) => {
      const price = Number.isFinite(item.price) ? item.price : 0;
      const quantity = Number.isFinite(item.quantity) ? item.quantity : 1;
      return acc + (price * quantity);
    }, 0);

    const shippingCost = Number.isFinite(shipping) ? shipping : 10;
    const taxRate = Number.isFinite(tax) ? tax : 0;
    const tariffsValue = Number.isFinite(tariffs) ? tariffs : 0;
    const taxesValue = Math.round(((subtotal + tariffsValue) * (taxRate / 100) + Number.EPSILON) * 100) / 100;
    const total = Math.round((subtotal + tariffsValue + shippingCost + taxesValue + Number.EPSILON) * 100) / 100;

    // Enhanced validation with detailed logging
    const values = { subtotal, shippingCost, taxes: taxesValue, tariffs: tariffsValue, total };
    if (!Object.values(values).every(val => Number.isFinite(val))) {
      console.error('Invalid checkout values:', values);
      console.error('Cart items:', cartItems);
      console.error('Special fees:', specialFees);
      toast.error('Invalid cart totals. Please refresh and try again.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      return;
    }

    const requestBody = {
      poNumber,
      moreDetails,
      billingAddress,
      zipCode,
      shippingAddress,
      subtotal,
      shipping_cost: shippingCost,
      taxes: taxesValue,
      tariffs: tariffsValue,
      total,
      delivery_time: null,
    };

    setIsLoading(true);
    try {
      const response = await fetch('https://valleyapi-dr9y.onrender.com/api/cart/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create order';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
            console.error('Server error details:', errorData);
          } else {
            const text = await response.text();
            console.error('Non-JSON response received:', text);
            errorMessage = `Server returned ${response.status}: ${text.slice(0, 100)}...`;
          }
        } catch (parseError) {
          console.error('Error parsing response:', parseError);
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const { idorders } = data;

      const checkoutResponse = await fetch(`https://valleyapi-dr9y.onrender.com/api/cart/orders/${idorders}/initial-checkout`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          order_state: 'Waiting for Payment',
          PO_Number: poNumber || null,
          More_Details: moreDetails || null,
          billing_address: billingAddress || null,
          zipCode: zipCode || null,
          shipping_address: shippingAddress || null,
        }),
      });

      if (!checkoutResponse.ok) {
        let errorMessage = 'Failed to send confirmation email';
        try {
          const contentType = checkoutResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await checkoutResponse.json();
            errorMessage = errorData.message || errorMessage;
          } else {
            const text = await checkoutResponse.text();
            console.error('Non-JSON response received:', text);
            errorMessage = `Server returned ${checkoutResponse.status}: ${text.slice(0, 100)}...`;
          }
        } catch (parseError) {
          console.error('Error parsing response:', parseError);
          errorMessage = `Server error: ${checkoutResponse.status} ${checkoutResponse.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const checkoutData = await checkoutResponse.json();
      const notification = hasUnpricedItems || checkoutData.confirmationEmailSent === false ? toast.warn : toast.success;
      notification(
        hasUnpricedItems
          ? 'Order created. Payment will be enabled after pricing is completed.'
          : checkoutData.confirmationEmailSent === false
          ? 'Order created. The confirmation email is delayed, but you can continue to payment.'
          : 'Order created. Continue with secure payment.', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });

      setCartItems([]);
      window.dispatchEvent(new Event('cartUpdated'));
      navigate(`/checkout/${idorders}`);
    } catch (error) {
      console.error('Error during checkout:', error.message);
      toast.error(`Checkout failed: ${error.message}`, {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle PO Number and More Details input changes
  const handlePoNumberChange = (e) => {
    const value = e.target.value.slice(0, 30);
    setPoNumber(value);
  };

  const handleMoreDetailsChange = (e) => {
    const value = e.target.value.slice(0, 255);
    setMoreDetails(value);
  };

  // Fetch cart items when user and loading state are ready
  useEffect(() => {
    if (!loading) {
      fetchCartItems();
    }
  }, [user, loading]);

  // Calculate subtotal
  const subtotal = cartItems.reduce((acc, item) => {
    const price = Number.isFinite(item.price) ? item.price : 0;
    const quantity = Number.isFinite(item.quantity) ? item.quantity : 1;
    return acc + (price * quantity);
  }, 0);
  const hasUnpricedItems = cartItems.some((item) => !Number.isFinite(item.price) || item.price <= 0);

  // Calculate tariffs using useMemo for optimization
  const tariffs = useMemo(() => {
    return cartItems.reduce((acc, item) => {
      const matchingFee = specialFees.find(f => f.brand && item.brand && f.brand.toLowerCase() === item.brand.toLowerCase());
      if (matchingFee && Number.isFinite(matchingFee.fee)) {
        if (matchingFee.fee_type === 'Porcentage') {
          return acc + (item.price * item.quantity * (matchingFee.fee / 100));
        } else if (matchingFee.fee_type === 'USD') {
          return acc + (matchingFee.fee * item.quantity);
        }
      }
      return acc;
    }, 0);
  }, [cartItems, specialFees]);

  // Calculate total
  const displayedTariffs = Number.isFinite(tariffs) ? tariffs : 0;
  const displayedTaxRate = Number.isFinite(tax) ? tax : 0;
  const displayedTaxes = Math.round(((subtotal + displayedTariffs) * (displayedTaxRate / 100) + Number.EPSILON) * 100) / 100;
  const total = Math.round((subtotal + displayedTariffs + shipping + displayedTaxes + Number.EPSILON) * 100) / 100;

  return (
    <div>
      <Header />
      <div className="cart-page">
        {loading ? (
          <p className="cart-loading">Checking authentication...</p>
        ) : (
          <>
            <div className="cart-hero">
              <div>
                <span className="cart-eyebrow">Your selection</span>
                <h1 className="cart-title">Review your cart.</h1>
                <p className="cart-subtitle">
                  Confirm products and delivery information before creating your order.
                </p>
              </div>
              <div className="cart-progress" aria-label="Checkout progress">
                <span className="is-active"><b>1</b> Cart</span>
                <i />
                <span><b>2</b> Order</span>
                <i />
                <span><b>3</b> Payment</span>
              </div>
            </div>
            {isLoading ? (
              <div className="cart-loading"><span className="cart-loading-spinner" /><p>Loading your products…</p></div>
            ) : cartItems.length === 0 ? (
              <div className="cart-empty">
                <img
                  src={EmptyCart}
                  alt="Empty cart"
                  className="cart-empty-icon"
                />
                <span className="cart-eyebrow">Nothing here yet</span>
                <h2>Your cart is ready for a project.</h2>
                <p>Browse in-stock automation products or search a specific part number.</p>
                <button
                  className="cart-shop-now"
                  onClick={() => navigate('/search?q=*&inStock=true&page=1')}
                  aria-label="Continue shopping"
                >
                  Browse in-stock products <span>→</span>
                </button>
              </div>
            ) : (
              <div className="cart-content">
                <div className="cart-items">
                  <div className="cart-items-heading">
                    <div><span>Products</span><strong>{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</strong></div>
                    <button type="button" onClick={() => navigate('/search?q=*')}>Continue shopping <span>↗</span></button>
                  </div>
                  {cartItems.map(item => (
                    <div key={item.productId} className="cart-product-card">
                      <div className="cart-product-card-image-container">
                        <img
                          src={getImageUrl(item.imageUrl)}
                          alt={item.name}
                          className="cart-product-card-image"
                          onError={(e) => {
                            console.error(`Failed to load image for ${item.name}: ${getImageUrl(item.imageUrl)}`);
                            e.target.src = 'https://valleyapi-dr9y.onrender.com/data/placeholder.jpg';
                          }}
                        />
                      </div>
                      <div className="cart-product-card-details">
                        <div className="cart-product-card-info">
                          <h2 className="cart-product-card-about">{truncateText(item.brand, 20)}</h2>
                          <h1 className="cart-product-card-title">{truncateText(item.name, 52)}</h1>
                          <p className="cart-product-card-about-title">{truncateText(item.description, 30)}</p>
                          <div className="cart-product-card-price-container">
                            <span className="cart-product-card-price">
                              {Number.isFinite(item.price) && item.price > 0
                                ? `$${(item.price * item.quantity).toFixed(2)}`
                                : 'Price pending'}
                            </span>
                          </div>
                        </div>
                        <div className="cart-product-card-actions">
                          <div className="cart-product-card-quantity-container">
                            <span className="cart-product-card-label">Quantity: {item.quantity} </span>
                            <div className="cart-product-card-quantity-controls">
                              <span className="cart-product-card-stock">
                                In Stock: {item.stock > 0 ? item.stock : 0}
                                {item.stockWarning && (
                                  <span className="cart-product-card-stock-warning">
                                    {item.stockWarning}
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            aria-label={`Remove ${item.name} from cart`}
                            className="cart-product-card-remove-icon"
                            onClick={() => handleDeleteItem(item.productId, item.name)}
                            disabled={isLoading}
                          >
                            <i className="fas fa-trash-alt" />
                            <span>Remove</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="cart-summary">
                  <div className="cart-summary-heading">
                    <span>Order summary</span>
                    <h2>Delivery & totals</h2>
                  </div>
                  <div className="cart-summary-item cart-summary-line">
                    <span>Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="cart-summary-item cart-summary-line">
                    <span>Shipping</span>
                    <span>${(Number.isFinite(shipping) ? shipping : 10).toFixed(2)}</span>
                  </div>
                  <div className="cart-summary-item cart-summary-line">
                    <span>Tax ({displayedTaxRate.toFixed(2)}%)</span>
                    <span>${displayedTaxes.toFixed(2)}</span>
                  </div>
                  <div className="cart-summary-item cart-summary-line">
                    <span>Tariffs</span>
                    <span>${(Number.isFinite(tariffs) ? tariffs : 0).toFixed(2)}</span>
                  </div>
                  <div className="cart-summary-divider"><span>Order details</span></div>
                  <div className="cart-summary-item cart-summary-field">
                    <label htmlFor="billing-address">Billing Address</label>
                    <input
                      id="billing-address"
                      type="text"
                      value={billingAddress}
                      onChange={handleBillingAddressChange}
                      maxLength={255}
                      placeholder="Enter Billing Address"
                      className="cart-summary-input"
                      aria-label="Billing Address (max 255 characters)"
                    />
                  </div>
                  <div className="cart-summary-item cart-summary-field">
                    <label htmlFor="zip-code">Zip Code</label>
                    <input
                      id="zip-code"
                      type="text"
                      value={zipCode}
                      onChange={handleZipCodeChange}
                      maxLength={20}
                      placeholder="Enter Zip Code"
                      className="cart-summary-input"
                      aria-label="Zip Code (max 20 characters)"
                    />
                  </div>
                  <div className="cart-summary-item cart-summary-field">
                    <label htmlFor="shipping-address">Shipping Address</label>
                    <input
                      id="shipping-address"
                      type="text"
                      value={shippingAddress}
                      onChange={handleShippingAddressChange}
                      maxLength={255}
                      placeholder="Enter Shipping Address"
                      className="cart-summary-input"
                      aria-label="Shipping Address (max 255 characters)"
                    />
                  </div>
                  <div className="cart-summary-item cart-summary-field">
                    <label htmlFor="po-number">PO Number</label>
                    <input
                      id="po-number"
                      type="text"
                      value={poNumber}
                      onChange={handlePoNumberChange}
                      maxLength={30}
                      placeholder="Enter PO Number"
                      className="cart-summary-input"
                      aria-label="PO Number (max 30 characters)"
                    />
                  </div>
                  <div className="cart-summary-item cart-summary-field">
                    <label htmlFor="more-details">More Details</label>
                    <textarea
                      id="more-details"
                      value={moreDetails}
                      onChange={handleMoreDetailsChange}
                      maxLength={255}
                      placeholder="Enter additional details"
                      className="cart-summary-textarea"
                      aria-label="More Details (max 255 characters)"
                    />
                  </div>
                  <div className="cart-summary-total">
                    <span>{hasUnpricedItems ? 'Current priced total' : 'Total'}</span>
                    <span>{hasUnpricedItems ? 'Pending' : `$${total.toFixed(2)}`}</span>
                  </div>
                  <button
                    className="cart-checkout-button"
                    disabled={isLoading || cartItems.length === 0}
                    aria-label="Proceed to checkout"
                    onClick={handleCheckout}
                  >
                    {user ? 'Create order' : 'Sign in to continue'} <span>→</span>
                  </button>
                  <p className={`cart-secure-note ${hasUnpricedItems ? 'is-warning' : ''}`}>
                    <i className={`fas ${hasUnpricedItems ? 'fa-circle-exclamation' : 'fa-lock'}`} />
                    {hasUnpricedItems
                      ? 'One or more products need pricing. You can create the order, but payment will remain disabled until every price is entered.'
                      : 'Secure checkout. Final payment happens on the next step.'}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default Cart;
